import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ScenarioRunOutcome,
    ScenarioRunScenario,
    ScenarioRunSummary,
} from '../../api/api';
import { loadingStateType } from '../../routes/dashboard';
import { scenarioRunPath } from '../../utils/paths';
import { runBuildLabel, runDuration, runMomentShort } from '../../utils/scenarioRunFormat';
import { ScenarioRunStatus } from './ScenarioRunStatus';
import { StopRunButton } from './StopRunButton';

// The outcomes the API sorts a scenario into, in the order the legend lists them: from good to not-yet.
const OUTCOMES = [
    'passed',
    'warnings',
    'failed',
    'running',
    'skipped',
    'pending',
] as const;

export type Outcome = (typeof OUTCOMES)[number];

const plural = (count: number, word: string) =>
    `${count} ${word}${count === 1 ? '' : 's'}`;

// A scenario still pending once the run is over never got to run, which is a different thing from one that is
// waiting its turn.
const outcomeLabel = (outcome: Outcome, finished: boolean) =>
    outcome === 'warnings'
        ? 'with warnings'
        : outcome === 'pending'
        ? finished
            ? 'not run'
            : 'to go'
        : outcome;

export const asOutcome = (value?: string): Outcome =>
    (OUTCOMES as readonly string[]).includes(value ?? '')
        ? (value as Outcome)
        : 'running';

/**
 * The outcome of one scenario from the full state document, sorted the way the API sorts it for the list
 * (ScenarioRunOutcome.Of), so the bar on a run's own page matches its bar in the history.
 */
export const outcomeOfScenario = (
    scenario: ScenarioRunScenario
): ScenarioRunOutcome => {
    const warnings = (scenario.warnings ?? []).length;
    const errors = (scenario.errors ?? []).length;
    const status = (scenario.status ?? '').toLowerCase();

    const outcome: Outcome =
        errors > 0 || ['failed', 'timedout', 'aborted'].includes(status)
            ? 'failed'
            : status === 'passed'
            ? warnings > 0
                ? 'warnings'
                : 'passed'
            : status === 'skipped'
            ? 'skipped'
            : status === 'pending'
            ? 'pending'
            : 'running';

    return { id: scenario.id, outcome, warnings, errors };
};

/**
 * One entry per scenario. A run listed by an API from before the outcomes existed only has totals, and those are
 * spread out in the same order so the bar still says something.
 */
const scenariosOf = (run: ScenarioRunSummary): ScenarioRunOutcome[] => {
    if ((run.scenarios ?? []).length > 0) {
        return run.scenarios as ScenarioRunOutcome[];
    }

    const totals = run.totals ?? {};
    const repeat = (count: number | undefined, outcome: Outcome) =>
        Array.from({ length: count ?? 0 }, () => ({ outcome }));

    return [
        ...repeat(totals.passed, 'passed'),
        ...repeat(totals.failed, 'failed'),
        ...repeat(totals.running, 'running'),
        ...repeat(totals.skipped, 'skipped'),
        ...repeat(totals.pending, 'pending'),
    ];
};

const segmentTitle = (scenario: ScenarioRunOutcome, finished: boolean) => {
    const messages = [
        scenario.errors ? plural(scenario.errors, 'error') : '',
        scenario.warnings ? plural(scenario.warnings, 'warning') : '',
    ].filter(Boolean);

    return `${scenario.id ?? 'scenario'}: ${outcomeLabel(
        asOutcome(scenario.outcome),
        finished
    )}${messages.length ? ` (${messages.join(', ')})` : ''}`;
};

/**
 * How a run went: one segment per scenario in execution order, the run's status after it, the counts under it.
 *
 * A scenario is one segment whatever it logged - four warnings make one orange segment, not four - so the bar
 * keeps counting scenarios. What the run logged outside any scenario belongs to no segment and gets a line of its
 * own. The detail page leaves the counts out, because its tiles say the same thing in larger type.
 */
export const RunResult = ({
    run,
    legend = true,
}: {
    run: ScenarioRunSummary;
    legend?: boolean;
}) => {
    const finished = !!run.finishedAtUtc || !!run.stale;
    const scenarios = scenariosOf(run);
    const counts = OUTCOMES.map((outcome) => ({
        outcome,
        count: scenarios.filter(
            (scenario) => asOutcome(scenario.outcome) === outcome
        ).length,
    })).filter((entry) => entry.count > 0);

    const runErrors = run.runErrors ?? 0;
    const runWarnings = run.runWarnings ?? 0;

    return (
        <div className="arl-runs__result">
            <div className="arl-runs__line">
                <div
                    className="arl-runs__bar"
                    role="img"
                    aria-label={counts
                        .map(
                            (entry) =>
                                `${entry.count} ${outcomeLabel(
                                    entry.outcome,
                                    finished
                                )}`
                        )
                        .join(', ')}
                >
                    {scenarios.map((scenario, index) => (
                        <span
                            key={scenario.id ?? index}
                            className={`arl-runs__segment arl-runs__segment--${asOutcome(
                                scenario.outcome
                            )}`}
                            title={segmentTitle(scenario, finished)}
                        />
                    ))}
                </div>
                {/* A fixed width, so every bar in the list ends in the same place. */}
                <span className="arl-runs__verdict">
                    <ScenarioRunStatus
                        status={run.stale ? 'abandoned' : run.status}
                    />
                </span>
            </div>

            {legend && (
            <div className="arl-runs__legend">
                {counts.map((entry) => (
                    <span key={entry.outcome}>
                        <span
                            className={`arl-runs__dot arl-runs__segment--${entry.outcome}`}
                        />
                        {entry.count} {outcomeLabel(entry.outcome, finished)}
                    </span>
                ))}
                <span className="arl-runs__of">
                    of {plural(scenarios.length, 'scenario')}
                </span>
            </div>
            )}

            {(runErrors > 0 || runWarnings > 0) && (
                <div
                    className={`arl-runs__outside arl-runs__outside--${
                        runErrors > 0 ? 'error' : 'warning'
                    }`}
                >
                    <FontAwesomeIcon
                        icon={
                            runErrors > 0
                                ? 'circle-exclamation'
                                : 'triangle-exclamation'
                        }
                    />
                    {[
                        runErrors > 0 ? plural(runErrors, 'error') : '',
                        runWarnings > 0 ? plural(runWarnings, 'warning') : '',
                    ]
                        .filter(Boolean)
                        .join(' and ')}{' '}
                    outside a scenario
                </div>
            )}
        </div>
    );
};

export interface IScenarioRunTableProps {
    customerID?: string;
    environment?: string;
    runs: ScenarioRunSummary[];
    loadingState: loadingStateType;
    // Called once a stop has been asked for, so the list can read the run again straight away.
    onStopped?: () => void;
}

/**
 * The runs against one environment, newest first.
 *
 * A run is named by when it started rather than by its id: the id is generated and means nothing to a reader,
 * so it is only in the tooltip, for somebody who has to find the state document behind it.
 */
export const ScenarioRunTable = ({
    customerID,
    environment,
    runs,
    loadingState,
    onStopped,
}: IScenarioRunTableProps) => {
    if (loadingState === 'init' || loadingState === 'loading') {
        return (
            <p className="arl-runs__empty">
                <FontAwesomeIcon icon="spinner" spin /> Loading the runs...
            </p>
        );
    }

    if (loadingState === 'error') {
        return (
            <p className="arl-runs__empty">
                The runs could not be read. Check the browser console for
                details.
            </p>
        );
    }

    if (runs.length === 0) {
        return (
            <p className="arl-runs__empty">
                No run against this environment yet.
            </p>
        );
    }

    return (
        <table className="arl-runs">
            <thead>
                <tr>
                    <th>Run</th>
                    <th>Duration</th>
                    <th>Result</th>
                </tr>
            </thead>
            <tbody>
                {runs.map((run) => (
                    <tr key={run.runId}>
                        <td>
                            <Link
                                to={scenarioRunPath(
                                    customerID,
                                    environment,
                                    run.runId
                                )}
                                className="arl-runs__link"
                                title={`Run ${run.runId}`}
                            >
                                {runMomentShort(run.startedAtUtc)}
                            </Link>
                            <div className="arl-runs__sub">
                                {run.suiteName}
                                {run.dryRun && (
                                    <span className="arl-runs__tag">
                                        dry run
                                    </span>
                                )}
                                {run.buildVersion && (
                                    <span className="arl-runs__build">
                                        build {runBuildLabel(run.buildVersion)}
                                    </span>
                                )}
                            </div>
                            {/* Only for a run this dashboard carries: a command-line run is stopped where it runs. */}
                            {run.canStop && customerID && environment && run.runId && (
                                <StopRunButton
                                    customer={customerID}
                                    environment={environment}
                                    runId={run.runId}
                                    compact
                                    onStopped={onStopped}
                                />
                            )}
                        </td>
                        <td className="arl-runs__number">
                            {/*
                                An abandoned run never gets a finish time, and counting on to now would show
                                how long ago it died. Its last update is the closest thing to an end it has.
                            */}
                            {runDuration(
                                run.startedAtUtc,
                                run.finishedAtUtc ??
                                    (run.stale ? run.updatedAtUtc : undefined)
                            )}
                        </td>
                        <td>
                            <RunResult run={run} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
